import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Send, FileText, Trash2, Eye, Download, MailPlus, UserPlus, Upload, Sparkles, MoreHorizontal, Link2 } from "lucide-react";
import { apiFetch, API } from "@/utils/api";
import { getInvoiceDefaults } from "@/utils/companyProfile";
import { formatInvoiceLabel } from "@/utils/invoiceFormat";
import ReconciliationLinkDialog from "@/components/ReconciliationLinkDialog";
import InvoicePreviewDialog from "@/components/InvoicePreviewDialog";
import InvoiceForm, { formatCurrency, InstallmentsEditor } from "@/components/InvoiceEmittedForm";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileInvoices from "@/mobile/MobileInvoices";

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("es-ES");
}

function StatusBadge({ status }) {
  // "Cobrada" no es un status del ciclo (draft/issued/registered): el cobro
  // vive en payment_status y lo pinta PaymentBadge (roadmap 1.4).
  const styles = {
    draft: "bg-slate-100 text-slate-600",
    issued: "bg-amber-100 text-amber-700",
    emitted: "bg-amber-100 text-amber-700",
    registered: "bg-blue-100 text-blue-700",
  };
  const labels = {
    draft: "Borrador",
    issued: "Emitida",
    emitted: "Emitida",
    registered: "Contabilizada",
  };
  return (
    <Badge className={styles[status] || styles.draft} variant="secondary">
      {labels[status] || status}
    </Badge>
  );
}

function PaymentBadge({ status }) {
  const styles = {
    unpaid: "bg-rose-100 text-rose-700",
    partial: "bg-amber-100 text-amber-700",
    paid: "bg-emerald-100 text-emerald-700",
    compensated: "bg-slate-100 text-slate-700",
    overpaid: "bg-purple-100 text-purple-700",
  };
  const labels = {
    unpaid: "Sin cobrar",
    partial: "Parcial",
    paid: "Cobrada",
    compensated: "Compensada",
    overpaid: "Sobrecobrada",
  };
  return (
    <Badge className={styles[status] || styles.unpaid} variant="secondary">
      {labels[status] || status}
    </Badge>
  );
}

// A9: envío de la factura por email con el PDF adjunto. Asunto y cuerpo
// editables con defaults sensatos (el cuerpo reutiliza el "Texto para email").
function SendEmailDialog({ invoice, onClose, onSent }) {
  const [to, setTo] = useState(invoice.client_email || "");
  const [subject, setSubject] = useState(
    `Factura ${invoice.series ? invoice.series + "-" : ""}${invoice.invoice_number}`
  );
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch(`${API}/invoices/emitted/${invoice.invoice_id}/email-text`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d?.body) setBody(d.body);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [invoice.invoice_id]);

  const handleSend = async () => {
    if (!to.trim()) {
      toast.error("Indica el email del destinatario");
      return;
    }
    setSending(true);
    try {
      const res = await apiFetch(
        `${API}/invoices/emitted/${invoice.invoice_id}/send-email`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ to: to.trim(), subject, body }),
        }
      );
      if (res.ok) {
        toast.success(`Factura enviada a ${to.trim()}`);
        onSent();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo enviar el email", { duration: 8000 });
      }
    } catch {
      toast.error("Error de red al enviar");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="send-email-dialog">
        <DialogHeader>
          <DialogTitle>
            Enviar factura {formatInvoiceLabel(invoice.series, invoice.invoice_number)} por email
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Para *</Label>
            <Input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="cliente@empresa.com"
              data-testid="send-email-to"
            />
          </div>
          <div>
            <Label>Asunto</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="send-email-subject"
            />
          </div>
          <div>
            <Label>Mensaje</Label>
            <Textarea
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="send-email-body"
            />
          </div>
          <p className="text-xs text-slate-500">
            El PDF de la factura se adjunta automáticamente.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSend} disabled={sending} data-testid="send-email-submit">
            {sending ? "Enviando..." : "Enviar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Menú compacto de acciones por factura emitida: un único botón "Acciones"
// despliega todas las operaciones disponibles según el estado, en vez de una
// fila de iconos que obligaba a hacer scroll horizontal. Los diálogos de "Ver
// conciliación" y "Ver PDF" se controlan desde aquí (hideTrigger).
function EmittedRowActions({ inv, onDownload, onSendEmail, onRegister, onEdit, onDelete }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            data-testid={`actions-emitted-${inv.invoice_id}`}
          >
            <MoreHorizontal className="w-4 h-4" /> Acciones
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onSelect={() => setReconcileOpen(true)}>
            <Link2 className="w-4 h-4 text-slate-500" /> Ver conciliación
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setPreviewOpen(true)}>
            <FileText className="w-4 h-4 text-blue-600" /> Ver PDF
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onDownload(inv.invoice_id)}>
            <Download className="w-4 h-4 text-blue-600" /> Descargar PDF
          </DropdownMenuItem>
          {inv.status !== "draft" && (
            <DropdownMenuItem onSelect={() => onSendEmail(inv)}>
              <MailPlus className="w-4 h-4 text-indigo-600" /> Enviar por email
            </DropdownMenuItem>
          )}
          {!inv.accounting_entry_id && (
            <DropdownMenuItem onSelect={() => onRegister(inv.invoice_id)}>
              <Send className="w-4 h-4" /> Emitir factura
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => onEdit(inv)}>
            <Eye className="w-4 h-4" /> Ver / Editar
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => onDelete(inv.invoice_id)}
            className="text-red-600 focus:text-red-700"
          >
            <Trash2 className="w-4 h-4" /> Eliminar
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ReconciliationLinkDialog
        kind="invoice"
        id={inv.invoice_id}
        open={reconcileOpen}
        onOpenChange={setReconcileOpen}
        hideTrigger
      />
      <InvoicePreviewDialog
        url={`${API}/invoices/emitted/${inv.invoice_id}/pdf`}
        title={`Factura ${inv.series ? inv.series + "-" : ""}${inv.invoice_number}`}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        hideTrigger
      />
    </>
  );
}

// Confianza OCR: documents.ocr_result.confidence = {campo: "ok"|"low", "_summary": "ok"|"low"}
function OcrLowWarning() {
  return (
    <span
      className="ml-1 text-amber-600"
      title="Dato dudoso — revísalo"
      role="img"
      aria-label="Dato dudoso — revísalo"
    >
      ⚠
    </span>
  );
}

function EmittedOCRPreviewDialog({ document, queueRemaining = 1, clients, onCreateInvoice, onClose }) {
  const ocr = document?.ocr_result || {};
  const confidence = ocr.confidence || {};
  const isLow = (field) => confidence[field] === "low";
  // Anillo ámbar en el Input para datos extraídos con poca confianza
  const lowClass = (field) => (isLow(field) ? "border-amber-400 ring-1 ring-amber-300 bg-amber-50/50" : "");
  const [clientId, setClientId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [services, setServices] = useState([]);
  const [series, setSeries] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [concept, setConcept] = useState("");
  const [baseAmount, setBaseAmount] = useState(0);
  const [vatRate, setVatRate] = useState(21);
  const [irpfRate, setIrpfRate] = useState(0);
  const [currency, setCurrency] = useState("EUR");
  const [paymentMethod, setPaymentMethod] = useState("transfer");
  const [installments, setInstallments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!document) return;
    const o = document.ocr_result || {};
    // Auto-match cliente por NIF
    let matched = "";
    if (o.client_nif) {
      const norm = String(o.client_nif).replace(/\s/g, "").toUpperCase();
      const c = clients.find(cl => (cl.nif || "").replace(/\s/g, "").toUpperCase() === norm);
      if (c) matched = c.client_id;
    }
    setClientId(matched);
    setServiceId("");
    const inferredSeries = o.series || (o.invoice_date ? o.invoice_date.slice(0, 4) : "");
    setSeries(inferredSeries);
    setInvoiceNumber(o.invoice_number ?? "");
    setInvoiceDate(o.invoice_date || "");
    setDueDate(o.due_date || "");
    setConcept(o.concept || "");
    setBaseAmount(o.base_amount ?? 0);
    setVatRate(o.vat_rate ?? 21);
    setIrpfRate(o.irpf_rate ?? 0);
    setCurrency(o.currency || "EUR");
    setPaymentMethod("transfer");
    setInstallments([]);
    // Si el OCR no extrajo IVA/IRPF, aplicar los defaults fiscales del perfil de empresa
    let cancelled = false;
    if (o.vat_rate == null || o.irpf_rate == null) {
      getInvoiceDefaults().then((d) => {
        if (cancelled) return;
        if (o.vat_rate == null) setVatRate(d.vat_rate);
        if (o.irpf_rate == null) setIrpfRate(d.irpf_rate);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [document, clients]);

  useEffect(() => {
    if (!clientId) { setServices([]); return; }
    apiFetch(`${API}/crm/services?client_id=${clientId}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setServices(Array.isArray(data) ? data : []))
      .catch(() => setServices([]));
  }, [clientId]);

  const ocrTotal = Math.round(
    // `|| 0` (no `|| 21`): IVA 0 es legítimo y debe cuadrar con lo que se envía al guardar
    ((Number(baseAmount) || 0) * (1 + (Number(vatRate) || 0) / 100) -
      (Number(baseAmount) || 0) * ((Number(irpfRate) || 0) / 100)) * 100
  ) / 100;

  const handleSubmit = async () => {
    if (!clientId) { toast.error("Selecciona un cliente"); return; }
    if (paymentMethod === "transfer" && !dueDate) {
      toast.error("La fecha de vencimiento es obligatoria para pago por transferencia");
      return;
    }
    if (paymentMethod === "installments") {
      const sum = installments.reduce((acc, r) => acc + (parseFloat(r.amount) || 0), 0);
      if (Math.abs(sum - ocrTotal) > 0.01) {
        toast.error(`La suma de los plazos (${sum.toFixed(2)} €) no coincide con el total (${ocrTotal.toFixed(2)} €)`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await onCreateInvoice(document.document_id, {
        client_id: clientId,
        service_id: serviceId || null,
        series: series || null,
        invoice_number: invoiceNumber === "" ? null : Number(invoiceNumber),
        invoice_date: invoiceDate || null,
        due_date: paymentMethod === "installments" ? null : (dueDate || null),
        concept,
        base_amount: Number(baseAmount) || 0,
        vat_rate: Number(vatRate) || 0,
        irpf_rate: Number(irpfRate) || 0,
        client_country: "España",
        currency,
        payment_method: paymentMethod,
        payment_schedule: paymentMethod === "installments" ? installments : null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!document) return null;

  const ocrEmpty = !ocr.invoice_date && !ocr.client_name && !ocr.base_amount;

  return (
    <DialogContent
      className="max-w-2xl max-h-[90vh] overflow-y-auto"
      onInteractOutside={(e) => e.preventDefault()}
      onEscapeKeyDown={(e) => e.preventDefault()}
    >
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-blue-500" />
          Datos extraídos por OCR (factura emitida)
          {queueRemaining > 1 && (
            <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
              {queueRemaining} en cola
            </span>
          )}
        </DialogTitle>
      </DialogHeader>

      {ocrEmpty && (
        <div className="rounded-md bg-amber-50 text-amber-700 px-3 py-2 text-sm">
          Gemini no extrajo datos. Rellena los campos manualmente.
        </div>
      )}

      {confidence._summary === "low" && (
        <div
          className="rounded-md bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 text-sm"
          data-testid="ocr-low-confidence-banner"
        >
          Algunos datos se han extraído con poca confianza. Revísalos antes de crear la factura.
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label>Cliente *{(isLow("client_name") || isLow("client_nif")) && <OcrLowWarning />}</Label>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
            <SelectContent>
              {clients.map(c => (
                <SelectItem key={c.client_id} value={c.client_id}>
                  {c.name}{c.nif ? ` · ${c.nif}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {ocr.client_name && !clientId && (
            <p className="text-xs text-amber-600 mt-1">
              OCR detectó: <strong>{ocr.client_name}</strong>{ocr.client_nif ? ` (${ocr.client_nif})` : ""} — selecciónalo arriba (no hay match automático).
            </p>
          )}
          {ocr.client_name && clientId && (
            <p className="text-xs text-green-600 mt-1">Match auto: {ocr.client_name}</p>
          )}
        </div>
        <div className="col-span-2">
          <Label>Servicio (opcional)</Label>
          <Select value={serviceId || "__none"} onValueChange={(v) => setServiceId(v === "__none" ? "" : v)} disabled={!clientId}>
            <SelectTrigger><SelectValue placeholder="Sin servicio" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">— sin servicio —</SelectItem>
              {services.map(s => (
                <SelectItem key={s.service_id} value={s.service_id}>{s.service_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Serie{isLow("series") && <OcrLowWarning />}</Label>
          <Input className={lowClass("series")} value={series} onChange={e => setSeries(e.target.value)} placeholder="2025 / 2026" />
        </div>
        <div>
          <Label>Nº Factura{isLow("invoice_number") && <OcrLowWarning />}</Label>
          <Input className={lowClass("invoice_number")} type="number" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="auto si vacío" />
        </div>
        <div>
          <Label>Fecha emisión{isLow("invoice_date") && <OcrLowWarning />}</Label>
          <Input className={lowClass("invoice_date")} type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} />
        </div>
        <div>
          <Label>Método de pago</Label>
          <Select value={paymentMethod} onValueChange={setPaymentMethod}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="transfer">Transferencia</SelectItem>
              <SelectItem value="installments">Pago fraccionado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {paymentMethod !== "installments" && (
          <div>
            <Label>Vencimiento <span className="text-red-500">*</span>{isLow("due_date") && <OcrLowWarning />}</Label>
            <Input className={lowClass("due_date")} type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          </div>
        )}
        <div className="col-span-2">
          <Label>Concepto{isLow("concept") && <OcrLowWarning />}</Label>
          <Textarea className={lowClass("concept")} value={concept} onChange={e => setConcept(e.target.value)} rows={2} />
        </div>
        <div>
          <Label>Base imponible ({currency}){isLow("base_amount") && <OcrLowWarning />}</Label>
          <Input className={lowClass("base_amount")} type="number" step="0.01" value={baseAmount} onChange={e => setBaseAmount(e.target.value)} />
        </div>
        <div>
          <Label>Moneda{isLow("currency") && <OcrLowWarning />}</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className={lowClass("currency")}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="EUR">EUR</SelectItem>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="GBP">GBP</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>IVA %{isLow("vat_rate") && <OcrLowWarning />}</Label>
          <Input className={lowClass("vat_rate")} type="number" step="0.01" value={vatRate} onChange={e => setVatRate(e.target.value)} />
        </div>
        <div>
          <Label>IRPF %{isLow("irpf_rate") && <OcrLowWarning />}</Label>
          <Input className={lowClass("irpf_rate")} type="number" step="0.01" value={irpfRate} onChange={e => setIrpfRate(e.target.value)} />
        </div>
      </div>

      {paymentMethod === "installments" && (
        <InstallmentsEditor
          total={ocrTotal}
          value={installments}
          onChange={setInstallments}
        />
      )}

      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button onClick={handleSubmit} disabled={submitting || !clientId}>
          {submitting ? "Creando..." : "Crear factura"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function InvoicesEmittedDesktop() {
  const [invoices, setInvoices] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [sendEmailInvoice, setSendEmailInvoice] = useState(null);   // A9
  const [statusFilter, setStatusFilter] = useState("all");
  const [unlinkCount, setUnlinkCount] = useState(0);
  const [unlinkDialog, setUnlinkDialog] = useState(false);
  const [unlinkData, setUnlinkData] = useState([]);
  const [unlinkLoading, setUnlinkLoading] = useState(false);
  const [ocrQueue, setOcrQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const currentOcrDoc = ocrQueue[0] || null;
  const ocrDialogOpen = ocrQueue.length > 0;
  const popOcrQueue = () => setOcrQueue(q => q.slice(1));

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const response = await apiFetch(`${API}/documents/upload?invoice_type=emitted`, {
          method: "POST",
          body: formData,
        });
        if (response.ok) {
          const doc = await response.json();
          if (doc.ocr_status === "completed") {
            setOcrQueue(q => [...q, doc]);
          } else {
            toast.info(`${file.name}: subido, OCR en proceso o fallido.`);
          }
        }
      } catch (e) {
        toast.error(`Error al subir ${file.name}`);
      }
    }
    setUploading(false);
    if (files.length > 1) {
      toast.info(`${files.length} archivos subidos. Revisa el OCR de cada uno.`);
    }
  };

  const handleCreateFromEmittedOCR = async (documentId, payload) => {
    try {
      const response = await apiFetch(`${API}/documents/${documentId}/create-emitted-invoice`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const inv = await response.json();
        toast.success(`Factura ${formatInvoiceLabel(inv.series, inv.invoice_number)} creada`);
        popOcrQueue();
        fetchInvoices();
        fetchUnlinked();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Error al crear factura");
      }
    } catch (e) {
      toast.error("Error de red al crear factura");
    }
  };

  const fetchInvoices = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      // "Cobrada" no es un status en BD: se filtra por payment_status=paid,
      // que es lo que la conciliación bancaria escribe (roadmap 1.4).
      if (statusFilter === "collected") params.append("payment_status", "paid");
      else if (statusFilter !== "all") params.append("status", statusFilter);

      const response = await apiFetch(`${API}/invoices/emitted?${params}`);
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await apiFetch(`${API}/crm/clients`);
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const fetchUnlinked = useCallback(async () => {
    try {
      const response = await apiFetch(`${API}/invoices/emitted/unlinked`);
      if (response.ok) {
        const data = await response.json();
        setUnlinkCount(data.total);
        setUnlinkData(data.invoices);
      }
    } catch (error) {
      console.error("Error fetching unlinked:", error);
    }
  }, []);

  useEffect(() => {
    fetchUnlinked();
  }, [fetchUnlinked]);

  const handleAutoLink = async () => {
    const exactMatches = unlinkData
      .filter((inv) => inv.suggestion?.confidence === "exact")
      .map((inv) => ({ invoice_id: inv.invoice_id, client_id: inv.suggestion.client_id }));

    if (exactMatches.length === 0) {
      toast.info("No hay coincidencias exactas para vincular");
      return;
    }

    setUnlinkLoading(true);
    try {
      const response = await apiFetch(`${API}/invoices/emitted/bulk-link`, {
        method: "POST",
        body: JSON.stringify(exactMatches),
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.linked} factura(s) vinculadas`);
        fetchInvoices();
        fetchUnlinked();
      }
    } catch (error) {
      toast.error("Error al vincular");
    } finally {
      setUnlinkLoading(false);
    }
  };

  const handleManualLink = async (invoiceId, clientId) => {
    try {
      const response = await apiFetch(`${API}/invoices/emitted/bulk-link`, {
        method: "POST",
        body: JSON.stringify([{ invoice_id: invoiceId, client_id: clientId }]),
      });
      if (response.ok) {
        toast.success("Factura vinculada");
        fetchInvoices();
        fetchUnlinked();
      }
    } catch (error) {
      toast.error("Error al vincular");
    }
  };

  const handleSaveInvoice = async (formData) => {
    try {
      const url = editingInvoice
        ? `${API}/invoices/emitted/${editingInvoice.invoice_id}`
        : `${API}/invoices/emitted`;

      const response = await apiFetch(url, {
        method: editingInvoice ? "PUT" : "POST",
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const body = await response.json().catch(() => ({}));
        toast.success(editingInvoice ? "Factura actualizada" : "Factura creada");
        // Integridad §1.2: la edición de una registrada re-asienta automáticamente
        if (editingInvoice && body.reaccounted) {
          toast.info("Asiento contable recalculado con los nuevos importes");
        }
        setDialogOpen(false);
        setEditingInvoice(null);
        fetchInvoices();
      } else {
        // 409: cobros conciliados/compensada/sellada — el detalle señala el camino
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Error al guardar factura", { duration: 9000 });
      }
    } catch (error) {
      toast.error("Error al guardar factura");
    }
  };

  const handleRegisterInvoice = async (invoiceId) => {
    try {
      const response = await apiFetch(`${API}/invoices/emitted/${invoiceId}/register`, {
        method: "POST",
      });

      if (response.ok) {
        toast.success("Factura emitida y contabilizada");
        fetchInvoices();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al emitir la factura");
      }
    } catch (error) {
      toast.error("Error al registrar");
    }
  };

  // Facturas creadas desde OCR llevan ocr_reviewed: false hasta confirmar los datos
  const handleConfirmOcr = async (invoiceId) => {
    try {
      const response = await apiFetch(`${API}/invoices/${invoiceId}/confirm-ocr`, {
        method: "POST",
      });
      if (response.ok) {
        toast.success("Datos del OCR confirmados");
        fetchInvoices();
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || "Error al confirmar los datos");
      }
    } catch (error) {
      toast.error("Error al confirmar los datos");
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!confirm("Eliminar esta factura?")) return;

    try {
      const response = await apiFetch(`${API}/invoices/emitted/${invoiceId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        const body = await response.json().catch(() => ({}));
        toast.success(
          body.reverted_entries
            ? "Factura eliminada y su asiento revertido"
            : "Factura eliminada"
        );
        fetchInvoices();
      } else {
        // 409: conciliada, compensada o sellada — el detalle explica el camino
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Error al eliminar", { duration: 9000 });
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleDownloadPdf = async (invoiceId) => {
    try {
      const response = await apiFetch(`${API}/invoices/emitted/${invoiceId}/pdf`);
      if (response.ok) {
        const blob = await response.blob();
        if (blob.size === 0) {
          toast.error("El PDF generado esta vacio");
          return;
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const disposition = response.headers.get("content-disposition");
        const filename = disposition
          ? disposition.split("filename=")[1]?.replace(/"/g, "")
          : "factura.pdf";
        a.download = filename;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        // Delay cleanup to allow browser to process download
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          a.remove();
        }, 500);
        toast.success("PDF descargado");
      } else {
        const err = await response.json().catch(() => null);
        toast.error(err?.detail || "Error al generar el PDF");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Error al descargar");
    }
  };


  // --- Compensación de facturas ---
  const selectedInvoices = invoices.filter((inv) => selectedIds.has(inv.invoice_id));
  const compSum = Math.round(selectedInvoices.reduce((s, i) => s + (i.total_amount ?? 0), 0) * 100) / 100;
  const compSameClient = new Set(selectedInvoices.map((i) => i.client_id || i.client_name)).size === 1;
  const compClosed = selectedInvoices.some((i) => ["paid", "compensated"].includes(i.payment_status));
  const canCompensate = selectedInvoices.length >= 2 && compSameClient && !compClosed && Math.abs(compSum) <= 0.01;
  const compReason = selectedInvoices.length < 2 ? "Selecciona 2+ facturas"
    : !compSameClient ? "Deben ser del mismo cliente"
    : compClosed ? "Alguna ya está cerrada"
    : Math.abs(compSum) > 0.01 ? `Deben sumar 0 € (suman ${compSum.toFixed(2)} €)`
    : "";

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleCompensate = async () => {
    const ids = Array.from(selectedIds);
    const note = window.prompt("Nota opcional para la compensación (puedes dejarlo vacío):", "") || null;
    try {
      const response = await apiFetch(`${API}/invoices/emitted/compensate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invoice_ids: ids, note }),
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Facturas compensadas");
        setSelectedIds(new Set());
        fetchInvoices();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al compensar");
      }
    } catch (e) {
      toast.error("Error al compensar");
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-emitted-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Facturas Emitidas
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Gestiona las facturas de ventas
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={(e) => handleFileUpload(Array.from(e.target.files || []))}
            accept=".pdf"
            className="hidden"
            multiple
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-emitted-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? "Subiendo..." : "Subir factura(s)"}
          </Button>
          <Button onClick={() => { setEditingInvoice(null); setDialogOpen(true); }} data-testid="new-emitted-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Factura
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-end">
            <div className="w-48">
              <Label className="mb-2 block">Estado</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="emitted-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="issued">Emitida</SelectItem>
                  <SelectItem value="registered">Contabilizada</SelectItem>
                  <SelectItem value="collected">Cobrada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Unlinked invoices banner */}
      {unlinkCount > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-3 px-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-800">
              <UserPlus className="w-4 h-4" />
              <span className="text-sm font-medium">
                {unlinkCount} factura{unlinkCount !== 1 ? "s" : ""} sin vincular a cliente CRM
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-300 text-amber-800 hover:bg-amber-100"
              onClick={() => setUnlinkDialog(true)}
            >
              Vincular
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Barra de compensación: saldar facturas opuestas del mismo cliente */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm font-medium text-blue-900">
            {selectedIds.size} seleccionada(s){selectedInvoices.length ? ` — suma ${compSum.toFixed(2)} €` : ""}
          </span>
          <Button size="sm" onClick={handleCompensate} disabled={!canCompensate}>Compensar</Button>
          {!canCompensate && compReason && <span className="text-xs text-slate-500">{compReason}</span>}
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>Cancelar</Button>
        </div>
      )}

      {/* Invoices Table */}
      <Card>
        <CardContent className="p-0">
          <Table className="table-dense table-fixed min-w-[1050px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]"></TableHead>
                <TableHead className="w-[100px]">Fecha</TableHead>
                <TableHead className="w-[100px]">Numero</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Concepto</TableHead>
                <TableHead className="w-[110px] text-right">Base</TableHead>
                <TableHead className="w-[110px] text-right">Total</TableHead>
                <TableHead className="w-[120px]">Estado</TableHead>
                <TableHead className="w-[175px]">Cobro</TableHead>
                <TableHead className="w-[130px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8">
                    <div className="spinner mx-auto" />
                  </TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay facturas emitidas</p>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((inv) => (
                  <TableRow key={inv.invoice_id} data-testid={`emitted-row-${inv.invoice_id}`}>
                    <TableCell>
                      {!["paid", "compensated"].includes(inv.payment_status) && (
                        <Checkbox
                          checked={selectedIds.has(inv.invoice_id)}
                          onCheckedChange={() => toggleSelect(inv.invoice_id)}
                        />
                      )}
                    </TableCell>
                    <TableCell className="font-mono">
                      {formatDate(inv.invoice_date)}
                    </TableCell>
                    <TableCell className="font-mono font-medium">
                      {formatInvoiceLabel(inv.series, inv.invoice_number)}
                    </TableCell>
                    <TableCell className="font-medium">{inv.client_name}</TableCell>
                    <TableCell className="truncate" title={inv.concept}>{inv.concept}</TableCell>
                    <TableCell className={`numeric ${inv.base_amount < 0 ? "text-rose-600" : ""}`}>{formatCurrency(inv.base_amount)}</TableCell>
                    <TableCell className={`numeric font-medium ${inv.total_amount < 0 ? "text-rose-600" : ""}`}>{formatCurrency(inv.total_amount)}</TableCell>
                    <TableCell>
                      <StatusBadge status={inv.status} />
                      {inv.ocr_reviewed === false && (
                        <div className="mt-1">
                          <Badge
                            className="bg-amber-100 text-amber-700 border-amber-200 whitespace-nowrap"
                            variant="secondary"
                            data-testid={`ocr-pending-badge-${inv.invoice_id}`}
                          >
                            OCR — pendiente de revisión
                          </Badge>
                          <button
                            type="button"
                            onClick={() => handleConfirmOcr(inv.invoice_id)}
                            className="block text-[11px] text-amber-700 underline mt-0.5 hover:text-amber-900"
                            data-testid={`confirm-ocr-btn-${inv.invoice_id}`}
                          >
                            Confirmar datos
                          </button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <PaymentBadge status={inv.payment_status} />
                      {inv.payment_status === "partial" && (inv.currency ?? "EUR") === "EUR" && (
                        <div className="text-xs text-amber-600 mt-1">
                          Faltan {formatCurrency(inv.total_amount - (inv.amount_collected ?? 0))}
                        </div>
                      )}
                      {inv.installment_status && (
                        <div className="text-xs text-muted-foreground mt-1 leading-snug">
                          <div>
                            {inv.installment_status.installments.filter((p) => p.status === "paid").length}
                            /{inv.installment_status.installments.length} plazos cobrados
                          </div>
                          {inv.installment_status.next_due && (
                            <div>Próximo: {formatDate(inv.installment_status.next_due)} ({inv.installment_status.next_due_amount.toFixed(2)} €)</div>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <EmittedRowActions
                        inv={inv}
                        onDownload={handleDownloadPdf}
                        onSendEmail={setSendEmailInvoice}
                        onRegister={handleRegisterInvoice}
                        onEdit={(i) => { setEditingInvoice(i); setDialogOpen(true); }}
                        onDelete={handleDeleteInvoice}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* A9: enviar factura por email con PDF adjunto */}
      {sendEmailInvoice && (
        <SendEmailDialog
          invoice={sendEmailInvoice}
          onClose={() => setSendEmailInvoice(null)}
          onSent={() => {
            setSendEmailInvoice(null);
            fetchInvoices();
          }}
        />
      )}

      {/* OCR Preview Dialog — solo se cierra por botones (Cancelar / Crear) para no perder el doc al cambiar de ventana */}
      <Dialog open={ocrDialogOpen}>
        <EmittedOCRPreviewDialog
          document={currentOcrDoc}
          queueRemaining={ocrQueue.length}
          clients={clients}
          onCreateInvoice={handleCreateFromEmittedOCR}
          onClose={popOcrQueue}
        />
      </Dialog>

      {/* Invoice Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingInvoice ? "Ver Factura" : "Nueva Factura Emitida"}
            </DialogTitle>
          </DialogHeader>
          <InvoiceForm
            invoice={editingInvoice}
            onSave={handleSaveInvoice}
            onCancel={() => { setDialogOpen(false); setEditingInvoice(null); }}
            clients={clients}
            onClientCreated={(doc) => setClients((prev) => [...prev, doc])}
          />
        </DialogContent>
      </Dialog>

      {/* Unlinked Invoices Dialog */}
      <Dialog open={unlinkDialog} onOpenChange={setUnlinkDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-amber-600" /> Facturas sin vincular
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {unlinkData.some((inv) => inv.suggestion?.confidence === "exact") && (
              <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <span className="text-sm text-emerald-800">
                  {unlinkData.filter((inv) => inv.suggestion?.confidence === "exact").length} con coincidencia exacta
                </span>
                <Button
                  size="sm"
                  onClick={handleAutoLink}
                  disabled={unlinkLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {unlinkLoading ? "Vinculando..." : "Auto-vincular todas"}
                </Button>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Factura</TableHead>
                  <TableHead>Cliente (texto)</TableHead>
                  <TableHead>Sugerencia</TableHead>
                  <TableHead>Accion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unlinkData.map((inv) => (
                  <TableRow key={inv.invoice_id}>
                    <TableCell className="font-mono text-sm">
                      {formatInvoiceLabel(inv.series, inv.invoice_number)}
                      <div className="text-xs text-slate-400">{formatDate(inv.invoice_date)}</div>
                    </TableCell>
                    <TableCell className="text-sm">{inv.client_name}</TableCell>
                    <TableCell>
                      {inv.suggestion?.confidence === "exact" ? (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          {inv.suggestion.client_name}
                        </Badge>
                      ) : inv.suggestion?.confidence === "ambiguous" ? (
                        <Badge className="bg-amber-100 text-amber-700">Ambiguo</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-500">Sin match</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {inv.suggestion?.confidence === "exact" ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleManualLink(inv.invoice_id, inv.suggestion.client_id)}
                          className="text-emerald-600 text-xs"
                        >
                          Vincular
                        </Button>
                      ) : (
                        <Select onValueChange={(cid) => handleManualLink(inv.invoice_id, cid)}>
                          <SelectTrigger className="h-8 text-xs w-[160px]">
                            <SelectValue placeholder="Elegir cliente" />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map((c) => (
                              <SelectItem key={c.client_id} value={c.client_id}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default function InvoicesEmitted() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileInvoices initialTab="emitted" /> : <InvoicesEmittedDesktop />;
}
